import React, { useState } from 'react';
import { db } from './firebase';
import { collection, addDoc } from 'firebase/firestore';

function RegistrationWizard({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthday: '',
    gender: '',
    genderPreference: '',
    ageMin: '',
    ageMax: '',
    city: '',
    state: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (step < 8) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate all fields
      if (!formData.firstName || !formData.lastName || !formData.birthday || 
          !formData.gender || !formData.genderPreference || !formData.ageMin || 
          !formData.ageMax || !formData.city || !formData.state) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Save to Firestore
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthday: formData.birthday,
        gender: formData.gender,
        genderPreference: formData.genderPreference,
        ageMin: parseInt(formData.ageMin),
        ageMax: parseInt(formData.ageMax),
        city: formData.city,
        state: formData.state,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'users_web'), userData);
      
      onComplete();
    } catch (err) {
      console.error('Error saving user data:', err);
      setError('Failed to save your information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What's your first name?</h2>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First name"
              style={styles.input}
              autoFocus
            />
          </div>
        );
      case 2:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What's your last name?</h2>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last name"
              style={styles.input}
              autoFocus
            />
          </div>
        );
      case 3:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>When is your birthday?</h2>
            <input
              type="date"
              name="birthday"
              value={formData.birthday}
              onChange={handleChange}
              style={styles.input}
              autoFocus
            />
          </div>
        );
      case 4:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What's your gender?</h2>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              style={styles.select}
              autoFocus
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </div>
        );
      case 5:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What's your gender preference?</h2>
            <select
              name="genderPreference"
              value={formData.genderPreference}
              onChange={handleChange}
              style={styles.select}
              autoFocus
            >
              <option value="">Select preference</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="both">Both</option>
            </select>
          </div>
        );
      case 6:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What's your age preference?</h2>
            <div style={styles.ageContainer}>
              <div style={styles.ageInputGroup}>
                <label style={styles.label}>Minimum age</label>
                <input
                  type="number"
                  name="ageMin"
                  value={formData.ageMin}
                  onChange={handleChange}
                  placeholder="Min"
                  min="18"
                  max="100"
                  style={styles.ageInput}
                  autoFocus
                />
              </div>
              <div style={styles.ageInputGroup}>
                <label style={styles.label}>Maximum age</label>
                <input
                  type="number"
                  name="ageMax"
                  value={formData.ageMax}
                  onChange={handleChange}
                  placeholder="Max"
                  min="18"
                  max="100"
                  style={styles.ageInput}
                />
              </div>
            </div>
          </div>
        );
      case 7:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What city are you in?</h2>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              style={styles.input}
              autoFocus
            />
          </div>
        );
      case 8:
        return (
          <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>What state are you in?</h2>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="State"
              style={styles.input}
              autoFocus
            />
          </div>
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.firstName.trim() !== '';
      case 2: return formData.lastName.trim() !== '';
      case 3: return formData.birthday !== '';
      case 4: return formData.gender !== '';
      case 5: return formData.genderPreference !== '';
      case 6: return formData.ageMin !== '' && formData.ageMax !== '' && parseInt(formData.ageMin) <= parseInt(formData.ageMax);
      case 7: return formData.city.trim() !== '';
      case 8: return formData.state.trim() !== '';
      default: return false;
    }
  };

  return (
    <div style={styles.wizardContainer}>
      <div style={styles.wizardContent}>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${(step / 8) * 100}%` }}></div>
        </div>
        <div style={styles.stepIndicator}>
          Step {step} of 8
        </div>
        
        {error && <div style={styles.error}>{error}</div>}
        
        {renderStep()}
        
        <div style={styles.buttonContainer}>
          {step > 1 && (
            <button onClick={handleBack} style={styles.backButton}>
              Back
            </button>
          )}
          {step < 8 ? (
            <button 
              onClick={handleNext} 
              disabled={!canProceed()}
              style={{ ...styles.nextButton, opacity: canProceed() ? 1 : 0.5 }}
            >
              Next
            </button>
          ) : (
            <button 
              onClick={handleSubmit} 
              disabled={loading || !canProceed()}
              style={{ ...styles.submitButton, opacity: (loading || !canProceed()) ? 0.5 : 1 }}
            >
              {loading ? 'Saving...' : 'Complete Registration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wizardContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  wizardContent: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '40px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },
  progressBar: {
    width: '100%',
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    marginBottom: '20px'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#C60000',
    borderRadius: '2px',
    transition: 'width 0.3s ease'
  },
  stepIndicator: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '30px',
    fontSize: '14px'
  },
  stepContainer: {
    marginBottom: '30px'
  },
  stepTitle: {
    fontSize: '24px',
    marginBottom: '20px',
    color: '#333',
    textAlign: 'center'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  },
  ageContainer: {
    display: 'flex',
    gap: '15px'
  },
  ageInputGroup: {
    flex: 1
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#666',
    fontSize: '14px'
  },
  ageInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxSizing: 'border-box'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '30px'
  },
  backButton: {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  nextButton: {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#C60000',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginLeft: 'auto'
  },
  submitButton: {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#C60000',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    width: '100%'
  },
  error: {
    backgroundColor: '#fee',
    color: '#c33',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
    textAlign: 'center'
  }
};

export default RegistrationWizard;

